import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/store/app.reducer';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Observable, Subscription } from 'rxjs';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import * as HelpActions from 'src/app/store/help/help.actions';
import { selectSectionCurrent, selectSectionModify } from 'src/app/store/help/help.selector';
import { SectionDataDb } from 'src/app/model/help';

@Component({
  templateUrl: './helper-add-section.dialog.component.html',
})
export class HelperAddSectionDialogComponent implements OnInit, OnDestroy {

  sectionCurrent$: Observable<SectionDataDb | undefined>;
  sectionCurrentSub: Subscription | undefined;

  sectionModifySuccess$: Observable<boolean | undefined>;
  sectionModifySuccessSub: Subscription | undefined;

  sectionForm: FormGroup;
  section: SectionDataDb;
  adding = true;

  // design
  screenWidth: number;
  MIN_MD_WIDTH = 500;
  MAX_MD_WIDTH = 1120;
  MIN_WIDTH_COL4 = 1680;
  DELTA_ERROR_PIXEL = 7;
  MIN_TWO_BUTTONS_DIALOG_WIDTH = 575 + this.DELTA_ERROR_PIXEL;

  MIN_MD_WIDTH_EDIT = 600 + this.DELTA_ERROR_PIXEL;
  MIN_EXTENDED_INPUTTEXT = 750;

  constructor(
    private store: Store<AppState>,
    private ref: DynamicDialogRef,
    public config: DynamicDialogConfig,
  ) {
    this.initSelectors();
    this.store.dispatch(HelpActions.resetSectionForm());
  }

  ngOnInit(): void {
    this.subscribeAll();
    this.onResize();
    if (this.config.data !== undefined && this.config.data.sectionId !== undefined) {
      this.store.dispatch(HelpActions.getHelpSection({ sectionId: this.config.data.sectionId }));
      this.adding = false;
    } else {
      this.section = new SectionDataDb();
      this.adding = true;
    }
    this.buildForm();
  }

  // !! AddSezione funziona. ModificaSezione si rompe alla prima apertura ma legge correttamente alla seconda apertura del dialog
  buildForm(): void {
    this.sectionForm = new FormGroup({
      'KeyHelp': new FormControl(this.section !== undefined ? this.section.KeyHelp : '', Validators.required),
      'TitleIt': new FormControl(this.section !== undefined ? this.section.TitleIt : '', Validators.required),
      'TitleEn': new FormControl(this.section !== undefined ? this.section.TitleEn : '', Validators.required),
      'TitleEs': new FormControl(this.section !== undefined ? this.section.TitleEs : '', Validators.required),
      'TitleDe': new FormControl(this.section !== undefined ? this.section.TitleDe : '', Validators.required),
      'TitleFr': new FormControl(this.section !== undefined ? this.section.TitleFr : '', Validators.required),
      'TitleNl': new FormControl(this.section !== undefined ? this.section.TitleNl : '', Validators.required),
      'TitlePl': new FormControl(this.section !== undefined ? this.section.TitlePl : '', Validators.required),
      'TitleRu': new FormControl(this.section !== undefined ? this.section.TitleRu : '', Validators.required),
      'TitleSk': new FormControl(this.section !== undefined ? this.section.TitleSk : '', Validators.required)
    });
  }

  initSelectors(): void {
    this.sectionModifySuccess$ = this.store.select(selectSectionModify);
    this.sectionCurrent$ = this.store.select(selectSectionCurrent);
  }

  subscribeAll(): void {
    this.sectionModifySuccessSub = this.sectionModifySuccess$?.subscribe(
      (rowModifySuccess) => {
        if (rowModifySuccess) {
          this.ref.close('success');
        }
      }
    );
    this.sectionCurrentSub = this.sectionCurrent$.subscribe((sectionCurrent) => {
      if (sectionCurrent !== undefined) {
        this.section = sectionCurrent;
        this.buildForm();
        this.sectionCurrentSub?.unsubscribe();
      }
    });
  }

  ngOnDestroy(): void {
    this.sectionModifySuccessSub?.unsubscribe();
  }

  isRowText(): boolean {
    return this.f.ContentType.value === 'text';
  }

  isRowVideo(): boolean {
    return this.f.ContentType.value === 'video';
  }

  submit(): void {
    this.section = {
      ...this.section,
      KeyHelp: this.f.KeyHelp.value,
      TitleIt: this.f.TitleIt.value,
      TitleEn: this.f.TitleEn.value,
      TitleEs: this.f.TitleEs.value,
      TitleDe: this.f.TitleDe.value,
      TitleFr: this.f.TitleFr.value,
      TitleNl: this.f.TitleNl.value,
      TitlePl: this.f.TitlePl.value,
      TitleRu: this.f.TitleRu.value,
      TitleSk: this.f.TitleSk.value,
    };

    if (this.section.Id) {
      this.store.dispatch(HelpActions.editHelpSection({ section: this.section }));
    } else {
      this.store.dispatch(HelpActions.addHelpSection({ section: this.section }));
    }
  }

  get f(): any {
    return this.sectionForm.controls;
  }

  closeDialog(): void {
    this.ref.close();
  }

  // Design
  @HostListener('window:resize', ['$event'])
  onResize() {
    this.screenWidth = window.innerWidth;
  }

  onMobileEdit(): boolean {
    return (this.screenWidth <= this.MIN_MD_WIDTH_EDIT);
  }

  col12Responsive(): string {
    return (this.screenWidth <= this.MAX_MD_WIDTH) ? "col-12" : "col-12";
  }

  dynamicAutocomplete(): string {
    if (this.onMobileEdit()) {
      return "p-autocomplete-panel";
    }
    return "p-autocomplete-panel-right";
  }

  dynamicButtonWidth(): string {
    return (this.screenWidth <= this.MIN_TWO_BUTTONS_DIALOG_WIDTH) ? "w-full" : "w-16rem";
  }

  dynamicInputTextWidth(): string {
    return (this.onMobileEdit()) ? "w-full h-2_5rem" : "w-23rem h-2rem";
  }

  justifyContentStyle(): string {
    return this.onMobileEdit() ? "justify-content-center" : "justify-content-start";
  }

  // Errors management & Design Border | Input
  showErrorInvalidMessage(type: HelperAddSectionDialogComponent["f"]): boolean {
    return (type.errors !== null) && (type.errors !== undefined) && (type.dirty);
  }

  showErrorConfirmInvalidMessage(type: HelperAddSectionDialogComponent["f"], notEquivalentType: string): boolean {
    return ((type.errors !== null && type.errors !== undefined && type.dirty) || (this.sectionForm.errors !== null && this.sectionForm.errors[notEquivalentType] !== null && this.sectionForm.errors[notEquivalentType] && type.dirty));
  }

  showErrors(type: HelperAddSectionDialogComponent["f"], classType: string): string {
    let inputTextStyle = "";

    switch (classType) {
      case 'borderRow':
        if (this.onMobileEdit()) {
          return "row";
        }
        return ((type.errors !== null) && (type.dirty)) ? "border-bottom-1 border-50" : "border-bottom-1 border-100";
      case 'borderInput':
        if (this.onMobileEdit()) {
          inputTextStyle += "col-12 w-full p-0";
        }
        if ((type.errors !== null) && (type.dirty)) {
          inputTextStyle += " border-bottom-1 border-50";
        } else {
          inputTextStyle += " border-bottom-1 border-100";
        }
        return inputTextStyle;
      case 'label':
        if (this.onMobileEdit()) {
          inputTextStyle += "col-12 p-1";
        }
        if ((type.errors !== null) && (type.dirty)) {
          inputTextStyle += " text-50";
        }
        return inputTextStyle;
      default:
        return inputTextStyle;
    }
  }

  showErrorsConfirmFields(type: HelperAddSectionDialogComponent["f"], classType: string, notEquivalentType: string): string {
    let inputTextStyle = "";

    switch (classType) {
      case 'borderRow':
        if (this.onMobileEdit()) {
          return "row";
        }
        if ((type.errors !== null && type.dirty) || (this.sectionForm.errors !== null && this.sectionForm.errors[notEquivalentType] !== null && this.sectionForm.errors[notEquivalentType] && type.dirty)) {
          inputTextStyle += " border-bottom-1 border-50";
        } else {
          inputTextStyle += " border-bottom-1 border-100";
        }
        return inputTextStyle;
      case 'borderInput':
        if (this.onMobileEdit()) {
          inputTextStyle += "col-12 w-full p-0";
        }
        if ((type.errors !== null && type.dirty) || (this.sectionForm.errors !== null && this.sectionForm.errors[notEquivalentType] !== null && this.sectionForm.errors[notEquivalentType] && type.dirty)) {
          inputTextStyle += " border-bottom-1 border-50";
        } else {
          inputTextStyle += " border-bottom-1 border-100";
        }
        return inputTextStyle;
      case 'label':
        if (this.onMobileEdit()) {
          inputTextStyle += "col-12 p-1";
        }
        if ((type.errors !== null && type.dirty) || (this.sectionForm.errors !== null && this.sectionForm.errors[notEquivalentType] !== null && this.sectionForm.errors[notEquivalentType] && type.dirty)) {
          inputTextStyle += " text-50";
        }
        return inputTextStyle;
      default:
        return inputTextStyle;
    }
  }

  showErrorRequiredMessage(type: HelperAddSectionDialogComponent["f"]): boolean {
    return (type.errors !== null) && (type.dirty);
  }

  col4Responsive(fieldPosition: string): string {
    if (this.screenWidth >= this.MIN_WIDTH_COL4) {
      if (fieldPosition === 'left') {
        return "col-12 pl-6 pr-3";
      }
      if (fieldPosition === 'center') {
        return "col-12 px-5";
      }
      if (fieldPosition === 'right') {
        return "col-12 pl-3 pr-6";
      }
      return "col-12";
    } else {
      return (this.screenWidth >= this.MAX_MD_WIDTH) ? "col-12 px-12" : "col-12";
    }
  }
  dynamicInputTextFormat(classType: string): string {
    switch (classType) {
      case 'borderRow':
        if (this.screenWidth <= this.MIN_EXTENDED_INPUTTEXT) {
          return "row";
        }
        return "";
      case 'borderInput':
        if (this.screenWidth <= this.MIN_EXTENDED_INPUTTEXT) {
          return "col-12 w-full p-0";
        }
        return "";
      case 'label':
        if (this.screenWidth <= this.MIN_EXTENDED_INPUTTEXT) {
          return "col-12 p-1";
        }
        return "";
      case 'inputText':
        if (this.screenWidth <= this.MAX_MD_WIDTH) {
          if (this.screenWidth <= this.MIN_EXTENDED_INPUTTEXT) {
            return "w-full h-2_5rem"
          }
          return "w-20rem h-2rem";
        }
        return "w-13rem h-2rem";
      default:
        return "";
    }
  }
  marginResponsive(): string {
    return (this.screenWidth > this.MAX_MD_WIDTH) ? "mr-2_5rem" : "";
  }
}
